import { IoFilter } from "react-icons/io5";
import { Calendar } from "@/components/ui/calendar";
import FilterColumnDashboardDate from "../../FilterColunmDashboardDate";
import { format, parseISO } from "date-fns";
import { DateRange } from "react-day-picker";
import { useLeadFilters } from "@/modules/leads/hooks/useLeadFilters";
import { QuickFilters } from "@/common/states/store/types";

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: () => void;
}

export function FilterModal({ isOpen, onClose, onApply }: FilterModalProps) {
  const { filters, setFilters, resetFilters } = useLeadFilters();

  if (!isOpen) return null;

  const handleApplyAndClose = () => {
    onApply();
    onClose();
  };

  const handleDateChange = (dateRange: DateRange | undefined) => {
    setFilters({
      dateRange: {
        start: dateRange?.from?.toISOString() ?? "",
        end: dateRange?.to?.toISOString() ?? "",
      },
    });
  };

  const handleQuickFilterSelect = (
    start: string,
    end: string,
    value: string,
  ) => {
    setFilters({
      dateRange: {
        start,
        end,
      },
      quickFilter: value as QuickFilters,
    });
  };

  const dateRange: DateRange | undefined =
    filters.dateRange.start && filters.dateRange.end
      ? {
        from: parseISO(filters.dateRange.start),
        to: parseISO(filters.dateRange.end),
      }
      : undefined;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-center items-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md sm:max-w-lg lg:max-w-2xl">
        {/* Header fixo */}
        <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center gap-2">
              <IoFilter className="text-redPrimary flex-shrink-0" />
              Filtrar por data
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-red-500 text-2xl font-bold transition-colors p-1 rounded-full hover:bg-gray-100"
              aria-label="Fechar modal"
            >
              ×
            </button>
          </div>
          <p className="text-gray-500 text-sm mt-2">
            Selecione um período para filtrar os dados do dashboard
          </p>
        </div>

        {/* Conteúdo com scroll */}
        <div className="max-h-[70vh] overflow-y-auto px-4 sm:px-6 py-4 space-y-6">
          {/* Filtros Rápidos */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Filtros Rápidos
            </h3>
            <FilterColumnDashboardDate
              selected={filters.quickFilter}
              onSelect={handleQuickFilterSelect}
            />
          </div>

          {/* Calendário Responsivo */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Período Personalizado
            </h3>
            <div className="w-full">
              <div className="w-full sm:w-[360px] md:w-[420px] lg:w-[480px] mx-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                <Calendar
                  captionLayout="dropdown"
                  mode="range"
                  selected={dateRange}
                  onSelect={handleDateChange}
                  defaultMonth={dateRange?.from || dateRange?.to || new Date()}
                  className="w-full rounded-md"
                  numberOfMonths={1}
                />
              </div>
            </div>

            {/* Período Selecionado */}
            {(dateRange?.from || dateRange?.to) && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700 font-medium">
                  Período selecionado:{" "}
                  {dateRange?.from
                    ? format(dateRange.from, "dd/MM/yyyy")
                    : "Data inicial"}{" "}
                  até{" "}
                  {dateRange?.to
                    ? format(dateRange.to, "dd/MM/yyyy")
                    : "Data final"}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer fixo */}
        <div className="sticky bottom-0 z-20 bg-white border-t border-gray-200 px-4 sm:px-6 py-4 rounded-b-xl">
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-between">
            {(dateRange?.from || dateRange?.to) && (
              <button
                onClick={() => resetFilters()}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Limpar período
              </button>
            )}
            <button
              className="px-4 py-2 bg-locavibe text-white rounded-lg hover:bg-locavibe/90 transition-colors font-medium"
              onClick={handleApplyAndClose}
            >
              Aplicar Filtro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
